import { TUNING } from '~/config/tuning'
import { apiFetch } from '~/utils/api'
import { advanceStreak, buildDaily, dailyKey, guessReward, type DailyPuzzle } from '~/utils/daily'
import { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import type { Fb } from '~/composables/useFirebase'

/**
 * 📅 Daily Silhouette state (v4.03): storage + reactivity around the
 * pure logic in utils/daily. Device-local for now — streaks join cloud
 * sync when the anonymous-auth work lands (agreed with Javi 2026-08-04).
 *
 * A day's flow: up to 3 silhouette guesses → on solve, the statline
 * bonus round → done. Stardust lands through useStardust.award (so the
 * earned counter syncs for signed-in players); a 30th consecutive day
 * queues a FREE booster the title screen opens through the real pack
 * ceremony (`boosterQueued`).
 */
const STATE_KEY = 'elemental-dispatch-daily-v1'

/** 🔐 v6.34: the claim request in flight, if any. MODULE scope so a
 * remount of the (v-if'd) daily modal joins the existing flight instead
 * of racing a second claim against it. */
let claiming: Promise<void> | null = null
/** 🕛 v7.18: which day that flight asks about - a rollover starts its own. */
let claimingDay: string | null = null
/** 👤 v7.19: and for which account - another account starts its own. */
let claimingUid: string | null = null

interface DailyDayState {
  day: string
  guessesUsed: number
  wrongIds: string[]
  solved: boolean
  /** null until the silhouette resolves; then pending → won/lost. */
  statRound: 'pending' | 'won' | 'lost' | null
  done: boolean
  /** ✨ earned today (display). */
  earned: number
  /** 🔐 v6.34: the claim this device resolved for today, and the
   * account it belongs to — so a refresh resumes it instead of asking
   * again and reading its own claim as another device's. */
  claim?: DailyClaimStatus
  claimUid?: string | null
}

/** 📖 v4.98: one finished day's record, kept so the daily card can
 * browse back through past silhouettes and what they paid. */
export interface DailyDayRecord {
  solved: boolean
  guessesUsed: number
  stat: 'won' | 'lost' | null
  earned: number
  /** Another device owned that day's claim. */
  elsewhere?: boolean
}

interface DailyStore {
  today: DailyDayState | null
  streak: number
  lastDoneDay: string | null
  boosterQueued: number
  /** Lifetime days completed (the tracker's odometer). */
  daysPlayed: number
  /** 📖 v4.98: day key → how it went (pruned to the last 60 days). */
  history: Record<string, DailyDayRecord>
}

const EMPTY: DailyStore = { today: null, streak: 0, lastDoneDay: null, boosterQueued: 0, daysPlayed: 0, history: {} }

/** Records started only at v4.98 — days before that simply have none. */
const HISTORY_KEEP_DAYS = 60

// 🔐 v4.05, moved to utils/daily in v6.34 so shouldRequestClaim can be
// tested without Nuxt. NOT re-exported from here: Nuxt auto-imports both
// files, and two exports of one name is a build warning.

export function useDailyGame() {
  const store = useState<DailyStore>('daily-game', () => structuredClone(EMPTY))
  const loaded = useState('daily-game-loaded', () => false)
  const claimStatus = useState<DailyClaimStatus>('daily-claim', () => 'unknown')
  /** Which account `claimStatus` describes (v6.34). */
  const claimOwner = useState<string | null>('daily-claim-owner', () => null)
  /** 🕛 v7.16: which DAY it describes - a verdict expires with the day. */
  const claimDay = useState<string | null>('daily-claim-day', () => null)
  /** 🕛 v7.16 (code review): the local day, reactive. ensureToday() moves it,
   * so a card left open across midnight follows it (the puzzle below used
   * to read `new Date()` once and keep yesterday's silhouette). */
  const dayKey = useState<string>('daily-day-key', () => dailyKey(new Date()))
  const stardust = useStardust()

  /** Ask the server for the account's one claim of today. Resolves the
   * status the UI gates play on; runs once per session, per account. */
  async function beginClaim() {
    const day = ensureToday()
    // 🕛 v7.16 (code review): the session's verdict was keyed by account
    // only, so a tab left open across midnight kept yesterday's answer -
    // 'duplicate' locked the new day out, 'granted' skipped its claim.
    if (claimDay.value !== day.day) {
      claimStatus.value = 'unknown'
      claimOwner.value = null
      claimDay.value = day.day
    }
    if (day.done) { claimStatus.value = 'local'; return }
    // 🔐 v6.34: one flight at a time. The modal is v-if'd, so closing
    // and reopening it remounts and re-enters here — and two claims in
    // the air means the second is told 'duplicate' and forfeits the day.
    // 🕛 v7.18 (follow-up review): ...but only a flight for THIS day. A
    // rollover used to join yesterday's request, whose late answer then
    // landed on today's card.
    // 👤 v7.19 (v7.18 review): ...and for THIS account. Switching accounts
    // mid-flight used to join the previous account's request and take its
    // answer. Auth settles here first (the v4.19 lesson below), so "no
    // user" is a real signed-out player.
    const fb = await firebaseReady()
    await fb.auth.authStateReady()
    const uid = fb.auth.currentUser?.uid ?? null
    if (claiming && claimingDay === day.day && claimingUid === uid) return claiming
    claimingDay = day.day
    claimingUid = uid
    const mine: Promise<void> = runClaim(day, fb, uid).finally(() => {
      if (claiming === mine) { claiming = null; claimingDay = null; claimingUid = null }
    })
    claiming = mine
    return mine
  }

  async function runClaim(day: DailyDayState, fb: Fb, uid: string | null) {
    // 🕳️ v4.19 (Javi: "I did the daily on my PC and my phone and got
    // double"): the auto-pop fires this within ms of page load, BEFORE
    // Firebase has restored the signed-in session — currentUser read
    // null, the claim was skipped, and a signed-in phone played
    // device-local with full rewards. beginClaim waits for auth to SETTLE
    // before calling this, so `uid` is the real answer.
    // 🕛 v7.18: a day that ended while we waited has its own flight;
    // 👤 v7.19: so does an account that signed in meanwhile.
    const stale = () => store.value.today?.day !== day.day || (fb.auth.currentUser?.uid ?? null) !== uid
    const user = uid ? fb.auth.currentUser : null
    // The session's answer only counts for the account it was resolved
    // for — sign out, sign in as someone else, and it must be re-asked.
    if (claimStatus.value !== 'unknown' && claimOwner.value === uid) return
    if (!user || !uid) { claimStatus.value = 'local'; claimOwner.value = null; return }
    if (!shouldRequestClaim({ done: day.done, claim: day.claim, claimUid: day.claimUid, uid })) {
      // This account already won today's claim on this device: resume
      // it rather than ask again and be told 'duplicate' by our own
      // earlier self.
      claimStatus.value = 'granted'
      claimOwner.value = uid
      return
    }
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{ granted: boolean }>('/api/daily/claim', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { day: day.day, epoch: PROGRESS_EPOCH },
      })
      if (stale()) return // yesterday's answer is nobody's business today
      claimStatus.value = res.granted ? 'granted' : 'duplicate'
      claimOwner.value = uid
      // Bank the verdict on TODAY's card (it expires with the day).
      day.claim = claimStatus.value
      day.claimUid = uid
      persist()
      if (!res.granted) markClaimedElsewhere(day)
    } catch (error) {
      // Offline or endpoint down: never brick the daily — play local.
      // Deliberately NOT banked: a retry when the network returns must
      // still be able to win the real claim.
      console.warn('Daily claim unavailable — playing device-local.', error)
      if (stale()) return
      claimStatus.value = 'local'
      claimOwner.value = uid
    }
  }

  /** Another device owns today: close the day HERE without rewards and
   * without touching the streak (the claiming device advanced it). */
  function markClaimedElsewhere(day: DailyDayState) {
    day.done = true
    day.earned = 0
    recordDay(day.day, { solved: false, guessesUsed: 0, stat: null, earned: 0, elsewhere: true })
    persist()
  }

  /** 📖 v4.98: bank the day's record and prune the tail. */
  function recordDay(dayKey: string, record: DailyDayRecord) {
    if (!store.value.history) store.value.history = {}
    store.value.history[dayKey] = record
    const cutoff = dailyKey(new Date(Date.now() - HISTORY_KEEP_DAYS * 86400000))
    for (const key of Object.keys(store.value.history)) {
      if (key < cutoff) delete store.value.history[key]
    }
  }

  function persist() {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STATE_KEY, JSON.stringify(store.value))
  }

  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    try {
      const raw = JSON.parse(localStorage.getItem(STATE_KEY) ?? 'null') as DailyStore | null
      if (raw && typeof raw === 'object') store.value = { ...structuredClone(EMPTY), ...raw }
    } catch { /* corrupt: start fresh */ }
    // A new local day retires yesterday's card (streak already banked).
    if (store.value.today && store.value.today.day !== dailyKey(new Date())) {
      store.value.today = null
      persist()
    }
  }

  /** Today's puzzle — derived, never stored (same seed = same answer). */
  const puzzle = computed<DailyPuzzle>(() => {
    void dayKey.value // 🕛 v7.16: re-derived when the local day moves
    return buildDaily(new Date())
  })

  /** 🕛 v7.16: has the local day changed under an open card? Starts
   * today's record when it has; true so the caller re-asks the claim. */
  function rollover(): boolean {
    load()
    if (store.value.today?.day === dailyKey(new Date())) return false
    ensureToday()
    return true
  }

  function ensureToday(): DailyDayState {
    load()
    const key = dailyKey(new Date())
    if (dayKey.value !== key) dayKey.value = key
    if (!store.value.today || store.value.today.day !== key) {
      store.value.today = {
        day: key, guessesUsed: 0, wrongIds: [], solved: false,
        statRound: null, done: false, earned: 0,
      }
      persist()
    }
    return store.value.today
  }

  /** Answer a silhouette guess. Returns what happened for the UI. */
  function guess(defId: string): { correct: boolean; finished: boolean } {
    const day = ensureToday()
    if (day.done || day.solved || day.guessesUsed >= TUNING.daily.guessRewards.length) {
      return { correct: false, finished: true }
    }
    day.guessesUsed++
    if (defId === puzzle.value.answerId) {
      day.solved = true
      day.statRound = 'pending'
      const pay = guessReward(day.guessesUsed, true)
      day.earned += pay
      stardust.earnEvent({ kind: 'daily-guess', day: day.day, guessesUsed: day.guessesUsed, solved: true })
      persist()
      return { correct: true, finished: false }
    }
    day.wrongIds.push(defId)
    if (day.guessesUsed >= TUNING.daily.guessRewards.length) {
      const pay = guessReward(day.guessesUsed, false)
      day.earned += pay
      stardust.earnEvent({ kind: 'daily-guess', day: day.day, guessesUsed: day.guessesUsed, solved: false })
      completeDay(day)
      return { correct: false, finished: true }
    }
    persist()
    return { correct: false, finished: false }
  }

  /** Answer the statline bonus round. */
  function guessStats(index: number): { correct: boolean } {
    const day = ensureToday()
    if (day.statRound !== 'pending') return { correct: false }
    const correct = index === puzzle.value.statAnswer
    day.statRound = correct ? 'won' : 'lost'
    if (correct) {
      day.earned += TUNING.daily.statBonus
      stardust.earnEvent({ kind: 'daily-stat', day: day.day })
    }
    completeDay(day)
    return { correct }
  }

  /** Bank the day: streak, milestones, the 30-day booster. */
  function completeDay(day: DailyDayState) {
    if (day.done) return
    day.done = true
    const result = advanceStreak(store.value.streak, store.value.lastDoneDay, new Date())
    store.value.streak = result.streak
    store.value.lastDoneDay = day.day
    store.value.daysPlayed++
    if (result.milestoneStardust > 0) {
      day.earned += result.milestoneStardust
      stardust.earnEvent({ kind: 'daily-milestone', day: day.day, mark: result.streak })
    }
    if (result.boosterEarned) store.value.boosterQueued++
    recordDay(day.day, {
      solved: day.solved,
      guessesUsed: day.guessesUsed,
      stat: day.statRound === 'pending' ? null : day.statRound,
      earned: day.earned,
    })
    persist()
  }

  /** The title screen consumes one queued booster into the pack flow. */
  function takeQueuedBooster(): boolean {
    load()
    if (store.value.boosterQueued <= 0) return false
    store.value.boosterQueued--
    persist()
    return true
  }

  /** The next milestone ahead of the current streak, for the tracker. */
  const nextMilestone = computed(() => {
    const streak = store.value.streak
    const marks = Object.keys(TUNING.daily.milestones).map(Number)
    const nextMark = marks.filter(m => m > streak).sort((a, b) => a - b)[0]
    const nextBooster = (Math.floor(streak / TUNING.daily.boosterEvery) + 1) * TUNING.daily.boosterEvery
    // 🌍 v5.19: the label is translated at build time — consumers render
    // it verbatim inside 'title.daysTo'.
    const { t } = useLocale()
    if (nextMark !== undefined && nextMark < nextBooster) {
      return { at: nextMark, label: t('daily.milestoneStardust', { n: TUNING.daily.milestones[nextMark] ?? 0 }) }
    }
    return { at: nextBooster, label: t('daily.milestoneBooster') }
  })

  return { store, load, puzzle, rollover, ensureToday, guess, guessStats, takeQueuedBooster, nextMilestone, beginClaim, claimStatus }
}
